// src/utils/unitConversion.js

const unitTables = {
  distance: {
    mm: 0.001,
    cm: 0.01,
    dm: 0.1,
    m: 1,
    meter: 1,
    meters: 1,
    km: 1000,
    in: 0.0254,
    inch: 0.0254,
    ft: 0.3048,
    feet: 0.3048,
    yd: 0.9144,
  },
  area: {
    mm2: 1e-6,
    "mm²": 1e-6,
    cm2: 1e-4,
    "cm²": 1e-4,
    dm2: 0.01,
    m2: 1,
    "m²": 1,
    in2: 0.00064516,
    inch2: 0.00064516,
    ft2: 0.092903,
    feet2: 0.092903,
    yd2: 0.83612736,
    ha: 10000.0,
    acre: 4046.8564,
  },
  volume: {
    mm3: 1e-9,
    "mm³": 1e-9,
    cm3: 1e-6,
    "cm³": 1e-6,
    dm3: 0.001,
    m3: 1,
    "m³": 1,
    mL: 1e-6,
    ml: 1e-6,
    L: 0.001,
    l: 0.001,
    litre: 0.001,
    litres: 0.001,
    kL: 1,
    kl: 1,
    in3: 1.6387e-5,
    inch3: 1.6387e-5,
    ft3: 0.0283168,
    feet3: 0.0283168,
    "gal (US)": 0.00378541,
    gal: 0.00378541,
    gallon: 0.00378541,
    gallons: 0.00378541,
  },
  weight: {
    mg: 1e-6,
    g: 1e-3,
    kg: 1,
    t: 1000,
    tonne: 1000,
    tonnes: 1000,
    "US ton (907.185 kg)": 907.185,
    ton: 907.185,
    tons: 907.185,
    oz: 0.0283495,
    lb: 0.453592,
    kilogram: 1,
    kilograms: 1,
    gram: 1e-3,
    grams: 1e-3,
    pound: 0.453592,
    pounds: 0.453592,
  },
  time: {
    s: 1,
    min: 60,
    h: 3600,
    d: 86400,
    wk: 604800,
    mo: 2.628e6, // avg month in seconds
    yr: 3.154e7, // avg year in seconds
  },
  transport: {
    "t.km": 1,
    "t.mi": 1.60934,
    "t.nmi": 1.852,
  },
  energy: {
    J: 1,
    kJ: 1000,
    GJ: 1e9,
    Wh: 3600,
    kWh: 3.6e6,
    kwh: 3.6e6,
    MWh: 3.6e9,
    mwh: 3.6e9,
    Btu: 1055.06,
    mmBTU: 1.05506e9,
    mmbtu: 1.05506e9,
    therm: 1.05506e8,
    therms: 1.05506e8,
  },
  item: { unit: 1, units: 1, item: 1, items: 1 },
  passenger: { "passenger-kilometer": 1, "passenger-mile": 1.60934 },
  piece: { piece: 1, pieces: 1, pcs: 1, pc: 1, ea: 1, each: 1 },
  vehicule: { "vehicle-kilometer": 1, "vehicle-mile": 1.60934 },
  flow_rate: { "l/h": 1 },
};

/**
 * Fuel specific physical & thermal conversion constants
 * Natural gas: 1 m³ = 10.55 kWh (HHV 37.98 MJ / 3.6 MJ/kWh per Canada NIR & CER)
 * Diesel: 1 US gal = 3.78541 L
 */
export const FUEL_CONVERSIONS = {
  natural_gas: {
    m3_to_kwh: 10.55,
    kwh_to_m3: 1 / 10.55,
    m3_to_gj: 0.03798,
    gj_to_m3: 26.3296,
    kwh_to_gj: 0.0036,
    gj_to_kwh: 277.778,
    factors: {
      m3: 0.00193,   // tCO2e/m3 (Canada NIR: 1.93 kg/m3)
      kwh: 0.000183, // tCO2e/kWh (0.183 kg/kWh)
      gj: 0.0508,    // tCO2e/GJ
    },
    db_factors: {
      kwh: 0.21,     // kg CO2e/kWh (DB options table default)
      m3: 2.2155,    // 0.21 * 10.55 kg CO2e/m3
    },
  },
  diesel: {
    gal_to_l: 3.78541,
    l_to_gal: 1 / 3.78541,
    factors: {
      l: 0.00268,    // tCO2e/L (2.68 kg/L)
      gal: 0.01014,  // tCO2e/gal (10.14 kg/gal)
    },
    db_factors: {
      l: 2.68,       // kg CO2e/L
      gal: 10.1449,  // kg CO2e/gal
    },
  },
};

/**
 * Converts fuel activity values between energy, volume, and mass bases
 * @param {number} value
 * @param {"natural_gas"|"diesel"} fuelType
 * @param {string} fromUnit
 * @param {string} toUnit
 * @returns {number}
 */
export function ConvertFuel(value, fuelType = "natural_gas", fromUnit, toUnit) {
  if (value === null || value === undefined || isNaN(value)) return value;
  if (!fromUnit || !toUnit) return value;
  const f = fromUnit.toString().trim().toLowerCase().replace(/³/g, "3");
  const t = toUnit.toString().trim().toLowerCase().replace(/³/g, "3");
  if (f === t) return value;

  const ft = (fuelType || "").toLowerCase();

  const cleanPrecision = (num) => Math.round((num + Number.EPSILON) * 1e8) / 1e8;

  if (ft.includes("gas")) {
    const cfg = FUEL_CONVERSIONS.natural_gas;
    if (f === "m3" && t === "kwh") return cleanPrecision(value * cfg.m3_to_kwh);
    if (f === "kwh" && t === "m3") return cleanPrecision(value * cfg.kwh_to_m3);
    if (f === "m3" && t === "gj") return cleanPrecision(value * cfg.m3_to_gj);
    if (f === "gj" && t === "m3") return cleanPrecision(value * cfg.gj_to_m3);
    if (f === "kwh" && t === "gj") return cleanPrecision(value * cfg.kwh_to_gj);
    if (f === "gj" && t === "kwh") return cleanPrecision(value * cfg.gj_to_kwh);
  }

  if (ft.includes("diesel") || ft.includes("fuel") || ft.includes("ifta")) {
    const cfg = FUEL_CONVERSIONS.diesel;
    if ((f === "gal" || f === "gallon" || f === "gallons" || f.includes("gal")) && (t === "l" || t === "litre" || t === "litres")) {
      return cleanPrecision(value * cfg.gal_to_l);
    }
    if ((f === "l" || f === "litre" || f === "litres") && (t === "gal" || t === "gallon" || t === "gallons" || t.includes("gal"))) {
      return cleanPrecision(value * cfg.l_to_gal);
    }
  }

  return Convert(value, fromUnit, toUnit);
}

/**
 * Convert value between compatible units
 * @param {number} value - input value
 * @param {string} from - source unit (e.g., "cm3", "kWh")
 * @param {string} to - target unit (e.g., "m3", "J")
 * @returns {number} converted value
 */
export function Convert(value, from, to) {
  if (value === null || value === undefined || isNaN(value)) return value;
  if (!from || !to) return value;
  
  const f = from.toString().trim().toLowerCase().replace(/³/g, "3").replace(/²/g, "2");
  const t = to.toString().trim().toLowerCase().replace(/³/g, "3").replace(/²/g, "2");

  if (f === t) return value;

  const cleanPrecision = (num) => Math.round((num + Number.EPSILON) * 1e8) / 1e8;

  for (const [category, units] of Object.entries(unitTables)) {
    const fromUnit = Object.keys(units).find((u) => u.toLowerCase().replace(/³/g, "3").replace(/²/g, "2") === f);
    const toUnit = Object.keys(units).find((u) => u.toLowerCase().replace(/³/g, "3").replace(/²/g, "2") === t);
    
    if (fromUnit && toUnit) {
      const valueInBase = value * units[fromUnit]; // convert to base unit
      return cleanPrecision(valueInBase / units[toUnit]); // convert to target unit
    }
  }

  // Cross-domain fallback for natural gas m3 <-> kWh
  if ((f === "m3" && t === "kwh") || (f === "kwh" && t === "m3")) {
    return ConvertFuel(value, "natural_gas", from, to);
  }

  return value; // Fallback: return original value if no conversion found
}

export const distanceUnits = ["mm", "cm", "m", "in", "ft", "yd"];
export const areaUnits = ["mm2", "cm2", "m2", "in2", "ft2", "yd2", "ha", "acre"];
export const volumeUnits = ["mm3", "cm3", "m3", "mL", "L", "kL", "in3", "ft3", "gal (US)"];
export const weightUnits = ["mg", "g", "kg", "t", "US ton (907.185 kg)", "oz", "lb"];
export const timeUnits = ["s", "min", "h", "d", "wk", "mo", "yr"];
export const transportUnits = ["t.km", "t.mi", "t.nmi"];
export const energyUnits = ["J", "kJ", "GJ", "Wh", "kWh", "MWh", "Btu", "mmBTU", "therms"];
export const itemUnits = ["unit"];
export const passsengerUnits = ["passenger-kilometer", "passenger-mile"];
export const pieceUnits = ["piece", "pcs"];
export const vehiculeUnits = ["vehicle-kilometer", "vehicle-mile"];
export const flowRateUnits = ["l/h"];

const categoryCanonicalUnits = {
  distance: distanceUnits,
  area: areaUnits,
  volume: volumeUnits,
  weight: weightUnits,
  time: timeUnits,
  transport: transportUnits,
  energy: energyUnits,
  item: itemUnits,
  passenger: passsengerUnits,
  piece: pieceUnits,
  vehicule: vehiculeUnits,
  flow_rate: flowRateUnits,
};

export function GetUnits(u) {
  if (!u || typeof u !== "string") return [];
  const raw = u.trim();
  const unit = raw.toLowerCase();
  const normalized = unit.replace(/³/g, "3").replace(/²/g, "2");

  for (const [category, units] of Object.entries(unitTables)) {
    const keys = Object.keys(units).map((i) => i.toLowerCase());
    if (keys.includes(unit) || keys.includes(normalized)) {
      return categoryCanonicalUnits[category] || Object.keys(units); // Return canonical units if available
    }
  }

  return [];
}

export function GetUnitCategory(u) {
  if (!u || typeof u !== "string") return null;
  const raw = u.trim();
  const unit = raw.toLowerCase();
  const normalized = unit.replace(/³/g, "3").replace(/²/g, "2");
  for (const [category, units] of Object.entries(unitTables)) {
    const keys = Object.keys(units).map((i) => i.toLowerCase());
    if (keys.includes(unit) || keys.includes(normalized)) {
      return category; // Return the category of the unit
    }
  }

  return null;
}

export const allUnits = [
  ...distanceUnits,
  ...areaUnits,
  ...volumeUnits,
  ...weightUnits,
  ...timeUnits,
  ...transportUnits,
  ...energyUnits,
  ...itemUnits,
  ...passsengerUnits,
  ...pieceUnits,
  ...vehiculeUnits,
  ...flowRateUnits,
];
